import React from "react";
import { ArrowRight, Sparkles, CheckCircle, Zap } from "lucide-react";
import { useLocaleContext } from "../context/LocaleContext";
import { getTranslation } from "../utils/translations";
import { useAuth } from "./AuthProvider";
// PERFORMANCE: UserAvatarStack now uses static data - no Supabase queries, zero CLS
import { UserAvatarStack, TOTAL_USERS } from './UserAvatarStack';
// import { HeroStats } from './HeroStats';

export const Hero: React.FC = () => {
  const { locale } = useLocaleContext();
  const { user, signIn } = useAuth();

  // Helper function for localized text
  const t = (
    en: string,
    de: string,
    ptBR: string,
    msMY: string,
    es: string,
    fr: string,
    ja: string,
    id: string
  ) => {
    if (locale === "de") return de;
    if (locale === "pt-BR") return ptBR;
    if (locale === "ms-MY") return msMY;
    if (locale === "es") return es;
    if (locale === "fr") return fr;
    if (locale === "ja") return ja;
    if (locale === "id") return id;
    return en;
  };

  // Format numbers using the active locale's thousands separator (e.g. de "8.583", fr "8 583")
  const getLocaleCode = () => {
    switch (locale) {
      case "de": return "de-DE";
      case "pt-BR": return "pt-BR";
      case "ms-MY": return "ms-MY";
      case "es": return "es-ES";
      case "fr": return "fr-FR";
      case "ja": return "ja-JP";
      case "id": return "id-ID";
      default: return "en-US";
    }
  };

  const handleGetStarted = () => {
    // Use requestAnimationFrame to prevent forced reflow
    // This defers the scroll operation to the next frame, avoiding layout thrashing
    requestAnimationFrame(() => {
      const removerElement = document.getElementById("remover");
      if (removerElement) {
        removerElement.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    });
  };

  const handleSignUp = () => {
    signIn();
  };

  return (
    <section className="relative overflow-hidden py-16 md:py-20 lg:py-24">
      <div className="container mx-auto px-4">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Column - Content */}
          <div className="space-y-8 text-center lg:text-left">
            {/* REMOVED: Fazier badge from Hero to improve LCP */}
            {/* Badge now only appears in Footer (lazy loaded) */}

            {/* Main Heading */}
            <h1 className="font-bold text-neutral-900 dark:text-white leading-tight">
              <span className="block text-4xl md:text-5xl lg:text-6xl text-accent-600 dark:text-accent-400">
                {getTranslation(locale, "header.title")}
              </span>
              <span className="block text-2xl md:text-3xl lg:text-4xl mt-2">
                {getTranslation(locale, "header.subtitle")}
              </span>
            </h1>

            {/* Description */}
            <p className="text-lg md:text-xl text-neutral-600 dark:text-neutral-400 leading-relaxed max-w-2xl mx-auto lg:mx-0">
              {getTranslation(locale, "header.description")}
            </p>

            {/* Feature List */}
            <div className="space-y-4 text-left max-w-md mx-auto lg:mx-0">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-1">
                  <CheckCircle
                    size={20}
                    className="text-accent-600 dark:text-accent-500"
                  />
                </div>
                <span className="text-neutral-700 dark:text-neutral-300 text-lg">
                  {t(
                    "Removes invisible AI watermarks",
                    "Entfernt unsichtbare KI-Wasserzeichen",
                    "Remove marcas d'água invisíveis de IA",
                    "Membuang tanda air AI yang tidak kelihatan",
                    "Elimina marcas de agua invisibles de IA",
                    "Supprime les filigranes IA invisibles",
                    "見えないAI透かしを削除",
                    "Menghapus watermark AI tak terlihat"
                  )}
                </span>
              </div>
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-1">
                  <CheckCircle
                    size={20}
                    className="text-accent-600 dark:text-accent-500"
                  />
                </div>
                <span className="text-neutral-700 dark:text-neutral-300 text-lg">
                  {t(
                    "Instant detection and cleaning",
                    "Sofortige Erkennung und Bereinigung",
                    "Detecção e limpeza instantâneas",
                    "Pengesanan dan pembersihan segera",
                    "Detección y limpieza instantáneas",
                    "Détection et nettoyage instantanés",
                    "即座に検出して除去",
                    "Deteksi dan pembersihan instan"
                  )}
                </span>
              </div>
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-1">
                  <CheckCircle
                    size={20}
                    className="text-accent-600 dark:text-accent-500"
                  />
                </div>
                <span className="text-neutral-700 dark:text-neutral-300 text-lg">
                  {t(
                    "Private & secure - no data stored",
                    "Privat & sicher - keine Daten gespeichert",
                    "Privado e seguro - sem dados armazenados",
                    "Peribadi & selamat - tiada data disimpan",
                    "Privado y seguro - sin datos almacenados",
                    "Confidentiel et sécurisé - aucune donnée conservée",
                    "プライベートで安全 - データ保存なし",
                    "Privat dan aman - tidak ada data tersimpan"
                  )}
                </span>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center lg:justify-start pt-4">
              <button
                onClick={handleGetStarted}
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-accent-600 hover:bg-accent-700 text-white rounded-lg font-semibold text-lg shadow-sm hover:shadow transition-[background-color,box-shadow] duration-200"
              >
                <Zap size={20} className="flex-shrink-0" aria-hidden="true" />
                <span className="whitespace-nowrap">
                  {t(
                    "Get Started",
                    "Jetzt starten",
                    "Começar Agora",
                    "Mulakan",
                    "Comenzar",
                    "Commencer",
                    "今すぐ始める",
                    "Mulai Sekarang"
                  )}
                </span>
                <ArrowRight size={20} className="flex-shrink-0" aria-hidden="true" />
              </button>

              {/* CLS FIX: Always render button but hide with visibility when user is logged in.
                  This reserves layout space and prevents shift when auth loads. */}
              <button
                onClick={handleSignUp}
                className={`inline-flex items-center justify-center gap-2 px-8 py-4 bg-white dark:bg-neutral-800 text-neutral-900 dark:text-white rounded-lg font-semibold text-lg border border-neutral-200 dark:border-neutral-700 hover:bg-neutral-50 dark:hover:bg-neutral-700 ${user ? 'invisible' : ''}`}
                aria-hidden={!!user}
                tabIndex={user ? -1 : 0}
              >
                <img
                  src="/google-icon.svg"
                  alt="Google"
                  width="20"
                  height="20"
                  loading="eager"
                  decoding="async"
                  className="w-5 h-5 flex-shrink-0"
                />
                <span className="whitespace-nowrap">
                  {t(
                    "Sign in with Google",
                    "Mit Google anmelden",
                    "Entrar com Google",
                    "Log masuk dengan Google",
                    "Iniciar sesión con Google",
                    "Se connecter avec Google",
                    "Googleでログイン",
                    "Masuk dengan Google"
                  )}
                </span>
              </button>
            </div>

            {/* Social Proof - Static data, zero CLS */}
            <div className="flex justify-center lg:justify-start">
              <UserAvatarStack maxAvatars={5} />
            </div>
          </div>

          {/* Right Column - Visual - Hidden on mobile */}
          <div className="hidden lg:block relative px-8 lg:px-8 lg:pr-16 space-y-6">
            {/* Main Visual Card */}
            <div className="relative bg-white dark:bg-neutral-900 rounded-2xl shadow-xl border border-neutral-200 dark:border-neutral-800 p-8">
              <div className="relative space-y-6">
                {/* Header */}
                <div className="flex items-center gap-4">
                  <div className="relative p-3 rounded-lg bg-accent-50 dark:bg-accent-900/20 flex items-center justify-center border border-accent-100 dark:border-accent-800">
                    <img
                      src="/favicon-96x96.webp"
                      alt="Logo"
                      width="32"
                      height="32"
                      loading="eager"
                      fetchpriority="high"
                      decoding="async"
                      className="w-8 h-8"
                    />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-neutral-500 dark:text-neutral-400">
                      {t(
                        "Detected Watermarks",
                        "Erkannte Wasserzeichen",
                        "Marcas d'água Detectadas",
                        "Tanda Air Dijumpai",
                        "Marcas de Agua Detectadas",
                        "Filigranes détectés",
                        "検出された透かし",
                        "Watermark Terdeteksi"
                      )}
                    </div>
                    <div className="text-2xl font-bold text-neutral-900 dark:text-white">
                      {t(
                        "Example",
                        "Beispiel",
                        "Exemplo",
                        "Contoh",
                        "Ejemplo",
                        "Exemple",
                        "例",
                        "Contoh"
                      )}
                    </div>
                  </div>
                </div>

                {/* Sample Text with Highlights */}
                <div className="space-y-4">
                  <div className="p-4 bg-neutral-50 dark:bg-neutral-800/50 rounded-lg border border-neutral-200 dark:border-neutral-700">
                    <div className="text-sm text-neutral-600 dark:text-neutral-300 font-mono leading-relaxed">
                      {t(
                        "This sample paragraph contains",
                        "Dieser Beispielabsatz enthält",
                        "Este parágrafo de exemplo contém",
                        "Perenggan contoh ini mengandungi",
                        "Este párrafo de ejemplo contiene",
                        "Ce paragraphe d'exemple contient",
                        "このサンプル段落には",
                        "Paragraf contoh ini berisi"
                      )}
                      <span className="bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200 px-1 mx-0.5 rounded border border-yellow-200 dark:border-yellow-800">
                        ​
                      </span>
                      {t(
                        "hidden",
                        "unsichtbare",
                        "caracteres",
                        "aksara",
                        "caracteres",
                        "des caractères",
                        "見えない",
                        "karakter"
                      )}
                      <span className="bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200 px-1 mx-0.5 rounded border border-yellow-200 dark:border-yellow-800">
                        ​
                      </span>
                      {t(
                        "characters you can't see",
                        "Zeichen, die man nicht sieht",
                        "ocultos que você não vê",
                        "tersembunyi yang tidak kelihatan",
                        "ocultos que no puedes ver",
                        "cachés invisibles à l'œil",
                        "文字が含まれています",
                        "tersembunyi yang tidak terlihat"
                      )}
                      <span className="bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200 px-1 mx-0.5 rounded border border-yellow-200 dark:border-yellow-800">
                        ​
                      </span>
                    </div>
                  </div>

                  {/* Arrow */}
                  <div className="flex justify-center">
                    <div className="p-2 bg-neutral-100 dark:bg-neutral-800 rounded-full text-neutral-600 dark:text-neutral-400">
                      <ArrowRight size={20} className="rotate-90" aria-hidden="true" />
                    </div>
                  </div>

                  {/* Clean Text */}
                  <div className="p-4 bg-green-50 dark:bg-green-900/10 rounded-lg border border-green-200 dark:border-green-900/30">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle
                        size={16}
                        className="text-green-600 dark:text-green-500"
                      />
                      <span className="text-xs font-semibold text-green-700 dark:text-green-400">
                        {t(
                          "Cleaned",
                          "Bereinigt",
                          "Limpo",
                          "Dibersihkan",
                          "Limpiado",
                          "Nettoyé",
                          "クリーン済み",
                          "Dibersihkan"
                        )}
                      </span>
                    </div>
                    <div className="text-sm text-neutral-700 dark:text-neutral-300 font-mono leading-relaxed">
                      {t(
                        "This sample paragraph contains hidden characters you can't see",
                        "Dieser Beispielabsatz enthält unsichtbare Zeichen, die man nicht sieht",
                        "Este parágrafo de exemplo contém caracteres ocultos que você não vê",
                        "Perenggan contoh ini mengandungi aksara tersembunyi yang tidak kelihatan",
                        "Este párrafo de ejemplo contiene caracteres ocultos que no puedes ver",
                        "Ce paragraphe d'exemple contient des caractères cachés invisibles à l'œil",
                        "このサンプル段落には見えない文字が含まれています",
                        "Paragraf contoh ini berisi karakter tersembunyi yang tidak terlihat"
                      )}
                    </div>
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 gap-4 pt-6 border-t border-neutral-100 dark:border-neutral-800">
                  <div className="text-center">
                    <div className="text-xl font-bold text-neutral-900 dark:text-white">
                      {TOTAL_USERS.toLocaleString(getLocaleCode())}
                    </div>
                    <div className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mt-1">
                      {t("Users", "Nutzer", "Usuários", "Pengguna", "Usuarios", "Utilisateurs", "ユーザー", "Pengguna")}
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-xl font-bold text-neutral-900 dark:text-white">
                      100%
                    </div>
                    <div className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mt-1">
                      {t(
                        "In-browser",
                        "Im Browser",
                        "No navegador",
                        "Dalam Pelayar",
                        "En el navegador",
                        "Dans le navigateur",
                        "ブラウザ内",
                        "Di Browser"
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Chips - Centered below visual card */}
            <div className="flex flex-wrap items-center justify-center gap-3 mt-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white dark:bg-neutral-900 rounded-full text-neutral-600 dark:text-neutral-300 text-sm font-medium border border-neutral-200 dark:border-neutral-800 shadow-sm">
                <Sparkles size={16} className="text-accent-500" aria-hidden="true" />
                <span>
                  {t(
                    "Professional AI Text Cleaning",
                    "Professionelle KI-Textreinigung",
                    "Limpeza Profissional de Texto IA",
                    "Pembersihan Teks AI Profesional",
                    "Limpieza Profesional de Texto IA",
                    "Nettoyage professionnel de texte IA",
                    "プロのAIテキストクリーニング",
                    "Pembersihan Teks AI Profesional"
                  )}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* REMOVED: Animation keyframes and styles - not needed anymore */}
    </section>
  );
};
